import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { ConfigService } from 'src/app/service/config.service';
import { SegmentedTabOption } from 'src/app/micro-components/segmented-tabs/segmented-tabs.component';
import { CommonDataParamsService } from 'src/app/shared/utils/common-data-params.service';
import { INTERNAL_COCKPIT_SETTINGS } from 'src/app/shared/utils/cockpit-constants';
import {
  DEFAULT_LANGUAGES,
  NavLanguage,
  serializeLanguageParam,
} from 'src/app/shared/utils/language.constants';
import {
  CUSTOM_TIMEFRAME,
  DEFAULT_PERIOD,
  DEFAULT_TIMEFRAME,
  TIMEFRAME_PERIOD_MAP,
  TIMEFRAME_SEGMENTS,
} from './global-filters.constants';

/**
 * Single owner of the URL writes behind the persistent nav-bar controls
 * (timeframe and language). Everything applies instantly — there is no Apply
 * step, unlike the filters pop-up.
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalFiltersService {
  constructor(
    private router: Router,
    private config: ConfigService,
    private commonDataParams: CommonDataParamsService,
  ) {}

  /**
   * Segments the tenant actually has data for: capped by the hard
   * `maxTimeframe` and by how far back `setup.startDate` reaches. Custom is
   * always offered — its own range is validated in the date dialog.
   */
  segments(): SegmentedTabOption<string>[] {
    const availableDays = moment().diff(
      moment(this.config.appConfig.setup.startDate),
      'days',
    );
    const cap = Math.min(INTERNAL_COCKPIT_SETTINGS.maxTimeframe, availableDays);
    return TIMEFRAME_SEGMENTS.filter(
      (segment) =>
        segment.value === CUSTOM_TIMEFRAME || Number(segment.value) <= cap,
    );
  }

  /**
   * Which segment to highlight for the given `time`: the matching day count, the
   * custom sentinel for a `from--to` range, or null for a legacy value such as
   * `time=1` (Today) or `time=14`, which stay honoured but unselectable.
   */
  activeTimeframe(time?: string): string | null {
    if (!time) {
      return DEFAULT_TIMEFRAME;
    }
    if (time.includes('--')) {
      return CUSTOM_TIMEFRAME;
    }
    return TIMEFRAME_SEGMENTS.some((segment) => segment.value === time)
      ? time
      : null;
  }

  setTimeframe(time: string): void {
    this.applyTimeframe(
      time,
      TIMEFRAME_PERIOD_MAP[time]?.period ?? DEFAULT_PERIOD,
    );
  }

  /** `time` is a `YYYY-MM-DD--YYYY-MM-DD` range from the date dialog. */
  setCustomRange(time: string, period: string): void {
    this.applyTimeframe(time, period);
  }

  navLanguages(lang?: string): NavLanguage[] {
    const resolved = this.commonDataParams.getLanguageFilter({ lang });
    return DEFAULT_LANGUAGES.filter((code) => resolved.includes(code));
  }

  /**
   * Flips the nav languages. Extra languages picked in the filters pop-up ride
   * along untouched, so both EN and DE may be switched off as long as one of
   * those extras keeps the selection non-empty. Selecting nothing at all falls
   * back to the default set rather than filtering news down to no language.
   */
  setNavLanguages(current: string | undefined, next: NavLanguage[]): void {
    const extras = this.commonDataParams
      .getLanguageFilter({ lang: current })
      .filter((code) => !DEFAULT_LANGUAGES.includes(code as NavLanguage));
    const selected = [...next, ...extras];
    // An absent `lang` resolves back to the default set, so null is the
    // "both active again" state.
    const lang = selected.length ? serializeLanguageParam(selected) : null;
    this.router.navigate([], {
      queryParams: { lang },
      queryParamsHandling: 'merge',
    });
  }

  private applyTimeframe(time: string, period: string): void {
    this.router.navigate([], {
      // newsday narrows to a single day and would otherwise survive the merge
      // and override the timeframe the user just picked.
      queryParams: { time, period, newsday: null },
      queryParamsHandling: 'merge',
    });
  }
}
